// src/components/ui/button/button.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { ButtonProps } from '../../../types';
import { cn } from '../../../theme';

// Enhanced color configurations with better contrast ratios (WCAG AA compliant)
const colorClasses = {
  moss: {
    solid: 'bg-moss-500 hover:bg-moss-400 text-stone-900',
    outlined: 'border-2 border-moss-500 text-moss-300 hover:bg-moss-600/20 hover:border-moss-400',
    ghost: 'text-moss-300 hover:bg-moss-600/25 hover:text-moss-200',
  },
  ice: {
    solid: 'bg-ice-light hover:bg-ice-300 text-stone-900',
    outlined: 'border-2 border-ice-light text-ice-light hover:bg-ice-600/20 hover:border-ice-300',
    ghost: 'text-ice-light hover:bg-ice-600/25 hover:text-ice-300',
  },
  cave: {
    solid: 'bg-cave-400 hover:bg-cave-300 text-stone-900',
    outlined: 'border-2 border-cave-400 text-cave-300 hover:bg-cave-600/20 hover:border-cave-300',
    ghost: 'text-cave-300 hover:bg-cave-600/25 hover:text-cave-200',
  },
  jungle: {
    solid: 'bg-jungle-500 hover:bg-jungle-400 text-stone-900',
    outlined: 'border-2 border-jungle-500 text-jungle-300 hover:bg-jungle-600/20 hover:border-jungle-400',
    ghost: 'text-jungle-300 hover:bg-jungle-600/25 hover:text-jungle-200',
  },
  berry: {
    solid: 'bg-berry-600 hover:bg-berry-500 text-white',
    outlined: 'border-2 border-berry-600 text-berry-400 hover:bg-berry-700/20 hover:border-berry-500',
    ghost: 'text-berry-400 hover:bg-berry-700/25 hover:text-berry-300',
  },
  volcano: {
    solid: 'bg-volcano-600 hover:bg-volcano-500 text-white',
    outlined: 'border-2 border-volcano-600 text-volcano-400 hover:bg-volcano-700/20 hover:border-volcano-500',
    ghost: 'text-volcano-400 hover:bg-volcano-700/25 hover:text-volcano-300',
  },
  sunset: {
    solid: 'bg-sunset-500 hover:bg-sunset-400 text-stone-900',
    outlined: 'border-2 border-sunset-500 text-sunset-300 hover:bg-sunset-600/20 hover:border-sunset-400',
    ghost: 'text-sunset-300 hover:bg-sunset-600/25 hover:text-sunset-200',
  },
  stone: {
    solid: 'bg-stone-500 hover:bg-stone-400 text-bone-200',
    outlined: 'border-2 border-stone-500 text-stone-300 hover:bg-stone-600/20 hover:border-stone-400',
    ghost: 'text-stone-300 hover:bg-stone-600/25 hover:text-stone-200',
  },
  sky: {
    solid: 'bg-sky-500 hover:bg-sky-400 text-stone-900',
    outlined: 'border-2 border-sky-500 text-sky-300 hover:bg-sky-600/20 hover:border-sky-400',
    ghost: 'text-sky-300 hover:bg-sky-600/25 hover:text-sky-200',
  },
  bone: {
    solid: 'bg-bone-200 hover:bg-bone-100 text-stone-900',
    outlined: 'border-2 border-bone-200 text-bone-200 hover:bg-bone-300/20 hover:border-bone-100',
    ghost: 'text-bone-200 hover:bg-bone-300/25 hover:text-bone-100',
  },
};

const sizeClasses = {
  xs: 'text-xs px-2 py-1',
  sm: 'text-sm px-3 py-1.5',
  md: 'text-base px-4 py-2',
  lg: 'text-lg px-5 py-2.5',
  xl: 'text-xl px-6 py-3',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    variant = 'solid',
    colorTheme = 'moss',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled = false,
    icon,
    iconPosition = 'left',
    enhanced = false, // New prop to enable enhanced features (ripple, better animations)
    className,
    children,
    onClick,
    ...props
  }, ref) => {
    const colors = colorClasses[colorTheme as keyof typeof colorClasses] || colorClasses.moss;
    const sizeClass = sizeClasses[size as keyof typeof sizeClasses] || sizeClasses.md;
    
    const buttonClasses = cn(
      // Base styles
      'font-display uppercase tracking-wider transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-stone-900',
      sizeClass,
      fullWidth && 'w-full',
      
      // Enhanced vs Standard styling
      enhanced ? [
        'rounded-lg relative overflow-hidden',
        variant === 'solid' && [
          colors.solid,
          'shadow-lg hover:shadow-xl',
          'transform hover:scale-105 active:scale-95',
          `focus:ring-${colorTheme}-500`,
          'font-bold',
        ],
        variant === 'outlined' && [
          'bg-stone-800/40 backdrop-blur-sm',
          colors.outlined,
          'hover:shadow-md',
          'transform hover:scale-102 active:scale-98',
          `focus:ring-${colorTheme}-400`,
          'font-semibold',
        ],
        variant === 'ghost' && [
          'bg-transparent',
          colors.ghost,
          'hover:shadow-sm',
          `focus:ring-${colorTheme}-400/50`,
          'font-medium',
        ],
      ] : [
        'rounded-stone border-3 border-stone-900',
        variant === 'solid' && [
          colors.solid,
          'shadow-[0_4px_0_#201919]',
          'hover:translate-y-[-2px] hover:shadow-[0_6px_0_#201919]',
          'active:translate-y-[2px] active:shadow-[0_2px_0_#201919]',
        ],
        variant === 'outlined' && [
          'bg-transparent',
          colors.outlined,
        ],
        variant === 'ghost' && [
          'bg-transparent border-transparent',
          colors.ghost,
        ],
      ],
      
      // Stone variant (shared between both styles)
      variant === 'stone' && [
        enhanced ? [
          'bg-gradient-to-b from-stone-500 to-stone-700',
          'text-bone-100 font-bold',
          'border-2 border-stone-800',
          'shadow-[0_4px_0_#000,inset_0_1px_0_rgba(255,255,255,0.2)]',
          'hover:shadow-[0_6px_0_#000,inset_0_1px_0_rgba(255,255,255,0.3)]',
          'active:shadow-[0_2px_0_#000,inset_0_1px_0_rgba(0,0,0,0.3)]',
          'hover:translate-y-[-2px] active:translate-y-[2px]',
          'focus:ring-stone-400',
        ] : [
          'bg-gradient-to-b from-stone-400 to-stone-700',
          'text-bone-200',
          'shadow-[0_4px_0_#201919,inset_0_2px_0_rgba(255,255,255,0.1),inset_0_-2px_0_rgba(0,0,0,0.3)]',
          'text-shadow-[2px_2px_0_rgba(0,0,0,0.5)]',
          'hover:translate-y-[-2px] hover:shadow-[0_6px_0_#201919,inset_0_2px_0_rgba(255,255,255,0.2),inset_0_-2px_0_rgba(0,0,0,0.3)]',
          'active:translate-y-[2px] active:shadow-[0_2px_0_#201919,inset_0_2px_0_rgba(0,0,0,0.3),inset_0_-2px_0_rgba(255,255,255,0.1)]',
        ]
      ],
      
      // Disabled state
      (disabled || loading) && [
        enhanced ? [
          'opacity-60 cursor-not-allowed',
          'hover:scale-100 hover:translate-y-0',
          'bg-stone-700 text-stone-500 border-stone-600',
        ] : [
          'opacity-50 cursor-not-allowed',
        ]
      ],
      
      className
    );

    // Ripple effect for enhanced buttons
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (enhanced && !disabled && !loading) {
        const button = e.currentTarget;
        const ripple = document.createElement('span');
        const rect = button.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        
        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        ripple.classList.add('ripple');
        
        button.appendChild(ripple);
        
        setTimeout(() => {
          ripple.remove();
        }, 600);
      }
      
      onClick?.(e);
    };

    const content = (
      <>
        {loading ? (
          <span className="inline-flex items-center gap-2">
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="inline-block"
            >
              ⚙️
            </motion.span>
            <span>Loading...</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-2">
            {icon && iconPosition === 'left' && (
              <span className="inline-flex">{icon}</span>
            )}
            {children}
            {icon && iconPosition === 'right' && (
              <span className="inline-flex">{icon}</span>
            )}
          </span>
        )}
        
        {/* Enhanced hover effect overlay - only for enhanced mode */}
        {enhanced && !disabled && !loading && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-transparent to-white/10 opacity-0 hover:opacity-100 transition-opacity pointer-events-none"
          />
        )}
      </>
    );

    // Ripple styles for enhanced buttons
    const rippleStyles = enhanced ? (
      <style>{`
        .ripple {
          position: absolute;
          border-radius: 50%;
          transform: scale(0);
          animation: ripple 600ms ease-out;
          background-color: rgba(255, 255, 255, 0.3);
          pointer-events: none;
        }
        
        @keyframes ripple {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `}</style>
    ) : null;

    // Use motion.button for enhanced mode, regular button for standard
    if (enhanced) {
      return (
        <>
          {rippleStyles}
          <motion.button
            ref={ref}
            className={buttonClasses}
            disabled={disabled || loading}
            onClick={handleClick}
            whileHover={!disabled && !loading ? { scale: variant === 'solid' ? 1.05 : 1.02 } : {}}
            whileTap={!disabled && !loading ? { scale: 0.95 } : {}}
            {...props}
          >
            {content}
          </motion.button>
        </>
      );
    }

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={disabled || loading}
        onClick={handleClick}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';